import { useCallback, useMemo, useState } from 'react';
import { Column, useTableState } from '../table/index.js';
import { formatDateRange, formatDurationMinutes, parseDuration } from './ChartComponents.js';
import { UserAction } from './userActionEnum.js';
import {
  downloadStaffConsultationsCSV,
  downloadStaffDocumentsCSV,
  downloadStaffTasksCSV,
  downloadTeamConsultationsCSV,
  downloadTeamDocumentsCSV,
  downloadTeamTasksCSV,
} from './insightsCsvExport.js';

const emptyDocumentStaffMetrics = {
  totalDocs: 0,
  totalTime: 'N/A',
  avgTime: 'N/A',
  totalAssigned: 0,
  assigningTime: 'N/A',
  avgAssigningTime: 'N/A',
  totalWorked: 0,
  totalWorkTime: 'N/A',
};

const sumDocumentStaffWork = (rows) => {
  const filed = rows.reduce((sum, row) => sum + (row.totalFiledAway || 0), 0);
  const reviewMinutes = rows.reduce((sum, row) => sum + parseDuration(row.totalTimeInReview), 0);
  const assigned = rows.reduce((sum, row) => sum + (row.totalAssigned || 0), 0);
  const assigningMinutes = rows.reduce((sum, row) => sum + parseDuration(row.totalTimeAssigning), 0);
  const worked = rows.reduce((sum, row) => sum + (row.totalWorked || 0), 0);
  return { filed, reviewMinutes, assigned, assigningMinutes, worked };
};

const documentStaffMetricsFrom = (work) => ({
  totalDocs: work.filed,
  totalTime: formatDurationMinutes(work.reviewMinutes),
  avgTime: formatDurationMinutes(work.filed > 0 ? work.reviewMinutes / work.filed : 0),
  totalAssigned: work.assigned,
  assigningTime: formatDurationMinutes(work.assigningMinutes),
  avgAssigningTime: formatDurationMinutes(work.assigned > 0 ? work.assigningMinutes / work.assigned : 0),
  totalWorked: work.worked,
  totalWorkTime: formatDurationMinutes(work.reviewMinutes + work.assigningMinutes),
});

const useInsightsTables = ({
  data,
  taskData,
  consultationData,
  reviewData,
  staffAggregateData,
  consultationStaffAggregateData,
  teams,
  userIdToName,
  insightsType,
  currentProcessorState,
  currentTasksState,
  currentConsultationsState,
}) => {
  const [selectedStaffRows, setSelectedStaffRows] = useState(new Set());
  const [selectedTeamRows, setSelectedTeamRows] = useState(new Set());
  const [selectedTaskStaffRows, setSelectedTaskStaffRows] = useState(new Set());
  const [selectedTaskTeamRows, setSelectedTaskTeamRows] = useState(new Set());
  const [selectedConsultationStaffRows, setSelectedConsultationStaffRows] = useState(new Set());
  const [selectedConsultationTeamRows, setSelectedConsultationTeamRows] = useState(new Set());

  // Table state management for Staff Performance table
  const {
    sortState: staffTableSortState,
    setSortState: setStaffTableSortState,
    filterValues: staffTableFilterValues,
    setFilterValues: setStaffTableFilterValues,
    disabledFilters: staffTableDisabledFilters,
    setDisabledFilters: setStaffTableDisabledFilters,
    paginationState: staffTablePaginationState,
    setPaginationState: setStaffTablePaginationState
  } = useTableState('StaffInsightsTable');

  // Table state management for Team Performance table
  const {
    sortState: teamTableSortState,
    setSortState: setTeamTableSortState,
    filterValues: teamTableFilterValues,
    setFilterValues: setTeamTableFilterValues,
    disabledFilters: teamTableDisabledFilters,
    setDisabledFilters: setTeamTableDisabledFilters,
    paginationState: teamTablePaginationState,
    setPaginationState: setTeamTablePaginationState
  } = useTableState('TeamInsightsTable');

  // Table state management for Task Staff Performance table
  const {
    sortState: taskStaffTableSortState,
    setSortState: setTaskStaffTableSortState,
    filterValues: taskStaffTableFilterValues,
    setFilterValues: setTaskStaffTableFilterValues,
    disabledFilters: taskStaffTableDisabledFilters,
    setDisabledFilters: setTaskStaffTableDisabledFilters,
    paginationState: taskStaffTablePaginationState,
    setPaginationState: setTaskStaffTablePaginationState
  } = useTableState('TaskStaffInsightsTable');

  // Table state management for Task Team Performance table
  const {
    sortState: taskTeamTableSortState,
    setSortState: setTaskTeamTableSortState,
    filterValues: taskTeamTableFilterValues,
    setFilterValues: setTaskTeamTableFilterValues,
    disabledFilters: taskTeamTableDisabledFilters,
    setDisabledFilters: setTaskTeamTableDisabledFilters,
    paginationState: taskTeamTablePaginationState,
    setPaginationState: setTaskTeamTablePaginationState
  } = useTableState('TaskTeamInsightsTable');

  const {
    sortState: consultationStaffTableSortState,
    setSortState: setConsultationStaffTableSortState,
    filterValues: consultationStaffTableFilterValues,
    setFilterValues: setConsultationStaffTableFilterValues,
    disabledFilters: consultationStaffTableDisabledFilters,
    setDisabledFilters: setConsultationStaffTableDisabledFilters,
    paginationState: consultationStaffTablePaginationState,
    setPaginationState: setConsultationStaffTablePaginationState
  } = useTableState('ConsultationStaffInsightsTable');

  const {
    sortState: consultationTeamTableSortState,
    setSortState: setConsultationTeamTableSortState,
    filterValues: consultationTeamTableFilterValues,
    setFilterValues: setConsultationTeamTableFilterValues,
    disabledFilters: consultationTeamTableDisabledFilters,
    setDisabledFilters: setConsultationTeamTableDisabledFilters,
    paginationState: consultationTeamTablePaginationState,
    setPaginationState: setConsultationTeamTablePaginationState
  } = useTableState('ConsultationTeamInsightsTable');

  // Format average duration or return "N/A" if null - MEMOIZED
  const formatAverageDuration = useCallback((duration) => {
    if (!duration) return 'N/A';
    const minutes = parseDuration(duration);
    return minutes > 0 ? formatDurationMinutes(minutes) : 'N/A';
  }, []);

  // Get latest snapshot for pie chart from current processor state (real-time) - MEMOIZED
  const getCurrentStateSnapshot = useCallback(() => {
    if (!currentProcessorState) return null;
    const documentsUnassigned = currentProcessorState.documentsReadyForReview - currentProcessorState.documentsAssigned;
    return [
      { name: 'Assigned', value: currentProcessorState.documentsAssigned },
      { name: 'Unassigned', value: documentsUnassigned }
    ];
  }, [currentProcessorState]);

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  // Memoize metric values - these must be before the loading/error/empty checks
  const averageTimeToOpenText = useMemo(() => 
    reviewData ? formatAverageDuration(reviewData.averageTimeToOpen) : 'N/A',
    [reviewData, formatAverageDuration]
  );

  const averageTimeToReviewText = useMemo(() => 
    reviewData ? formatAverageDuration(reviewData.averageTimeToReview) : 'N/A',
    [reviewData, formatAverageDuration]
  );

  const averageTimeToFileAwayText = useMemo(() => 
    reviewData ? formatAverageDuration(reviewData.averageTimeToFileAway) : 'N/A',
    [reviewData, formatAverageDuration]
  );

  const averageRecordingDurationText = useMemo(() =>
    reviewData ? formatAverageDuration(reviewData.averageRecordingDuration) : 'N/A',
    [reviewData, formatAverageDuration]
  );

  const averageConsultationTimeToReviewText = useMemo(() =>
    reviewData ? formatAverageDuration(reviewData.averageTimeToReview) : 'N/A',
    [reviewData, formatAverageDuration]
  );

  // Calculate total document intake (sum of CREATE events)
  const totalDocumentIntake = useMemo(() => {
    if (!data || data.length === 0) return 0;
    
    let total = 0;
    data.forEach(snapshot => {
      const createAggregates = snapshot.durationSnapshot?.userActionToAggregate?.[UserAction.CREATE] || [];
      createAggregates.forEach(aggregate => {
        total += aggregate.count;
      });
    });
    
    return total;
  }, [data]);

  // Calculate total tasks created (sum of CREATE events from task data)
  const totalTasksCreated = useMemo(() => {
    if (!taskData || taskData.length === 0) return 0;
    
    let total = 0;
    taskData.forEach(snapshot => {
      const createCount = snapshot.durationSnapshot?.userActionToCount?.[UserAction.CREATE] || 0;
      total += createCount;
    });
    
    return total;
  }, [taskData]);

  const totalEncounters = useMemo(() => {
    if (!consultationData || consultationData.length === 0) return 0;

    let total = 0;
    consultationData.forEach(snapshot => {
      total += snapshot.durationSnapshot?.userActionToCount?.[UserAction.COMPLETE_RECORDING] || 0;
    });

    return total;
  }, [consultationData]);

  // Calculate staff metrics based on selected rows
  const staffMetrics = useMemo(() => {
    if (!staffAggregateData || staffAggregateData.length === 0) {
      return emptyDocumentStaffMetrics;
    }

    const dataToAggregate = selectedStaffRows.size === 0
      ? staffAggregateData  // All staff
      : staffAggregateData.filter(row => selectedStaffRows.has(row.userId));

    return documentStaffMetricsFrom(sumDocumentStaffWork(dataToAggregate));
  }, [staffAggregateData, selectedStaffRows]);

  // Aggregate staff data by teams (using Sets since users can be in multiple teams)
  const teamAggregateData = useMemo(() => {
    if (!staffAggregateData || staffAggregateData.length === 0 || !teams || teams.length === 0) {
      return [];
    }

    return teams.map(team => {
      // Find all staff members in this team
      const teamStaff = staffAggregateData.filter(staff => 
        team.userIds && team.userIds.includes(staff.userId)
      );

      // Aggregate using Sets to avoid double-counting if a user appears multiple times
      const uniqueUserIds = new Set(teamStaff.map(s => s.userId));
      
      const work = sumDocumentStaffWork(teamStaff);

      return {
        teamId: team.teamId,
        teamName: team.teamName,
        memberCount: uniqueUserIds.size,
        totalFiledAway: work.filed,
        totalTimeInReview: formatDurationMinutes(work.reviewMinutes),
        totalTimeMinutes: work.reviewMinutes,
        avgTimePerDoc: work.filed > 0 ? work.reviewMinutes / work.filed : 0,
        totalAssigned: work.assigned,
        assigningTimeMinutes: work.assigningMinutes,
        avgAssigningTime: work.assigned > 0 ? work.assigningMinutes / work.assigned : 0,
        totalWorkMinutes: work.reviewMinutes + work.assigningMinutes,
        totalWorked: work.worked,
      };
    });
  }, [staffAggregateData, teams]);

  // Calculate team metrics based on selected team rows
  const teamMetrics = useMemo(() => {
    if (!teamAggregateData || teamAggregateData.length === 0) {
      return emptyDocumentStaffMetrics;
    }

    const dataToAggregate = selectedTeamRows.size === 0
      ? teamAggregateData  // All teams
      : teamAggregateData.filter(row => selectedTeamRows.has(row.teamId));

    const work = {
      filed: dataToAggregate.reduce((sum, row) => sum + row.totalFiledAway, 0),
      reviewMinutes: dataToAggregate.reduce((sum, row) => sum + row.totalTimeMinutes, 0),
      assigned: dataToAggregate.reduce((sum, row) => sum + row.totalAssigned, 0),
      assigningMinutes: dataToAggregate.reduce((sum, row) => sum + row.assigningTimeMinutes, 0),
    };

    return documentStaffMetricsFrom(work);
  }, [teamAggregateData, selectedTeamRows]);

  // Combined metrics that consider both staff and team selections
  const combinedMetrics = useMemo(() => {
    // If both selections are empty, show all data
    if (selectedStaffRows.size === 0 && selectedTeamRows.size === 0) {
      return staffMetrics;
    }

    // Collect all unique user IDs from selections using Sets
    const selectedUserIdsSet = new Set();

    // Add directly selected staff
    selectedStaffRows.forEach(userId => selectedUserIdsSet.add(userId));

    // Add staff from selected teams
    if (selectedTeamRows.size > 0) {
      teams.forEach(team => {
        if (selectedTeamRows.has(team.teamId)) {
          team.userIds.forEach(userId => selectedUserIdsSet.add(userId));
        }
      });
    }

    // Filter staff data to only selected users
    const filteredStaff = staffAggregateData.filter(staff => 
      selectedUserIdsSet.has(staff.userId)
    );

    return documentStaffMetricsFrom(sumDocumentStaffWork(filteredStaff));
  }, [staffAggregateData, selectedStaffRows, selectedTeamRows, teams, staffMetrics]);

  // Define staff table columns
  const staffTableColumns = useMemo(() => [
    new Column({
      key: 'name',
      title: 'Name',
      sortable: true,
      filterable: true,
      dataType: 'text',
      accessor: (row) => userIdToName[row.userId] || row.userId
    }),
    new Column({
      key: 'totalWorked',
      title: 'Documents worked',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalWorked || 0
    }),
    new Column({
      key: 'totalDocs',
      title: 'Documents filed away',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalFiledAway
    }),
    new Column({
      key: 'totalTime',
      title: 'Time reviewing documents',
      sortable: true,
      filterable: false,
      accessor: (row) => parseDuration(row.totalTimeInReview),
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'avgTime',
      title: 'Average time reviewing each document',
      sortable: true,
      filterable: false,
      accessor: (row) => {
        const minutes = parseDuration(row.totalTimeInReview);
        return row.totalFiledAway > 0 ? minutes / row.totalFiledAway : 0;
      },
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'totalAssigned',
      title: 'Documents assigned',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalAssigned || 0
    }),
    new Column({
      key: 'assigningTime',
      title: 'Time assigning documents',
      sortable: true,
      filterable: false,
      accessor: (row) => parseDuration(row.totalTimeAssigning),
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'avgAssigningTime',
      title: 'Average time assigning each document',
      sortable: true,
      filterable: false,
      accessor: (row) => {
        const minutes = parseDuration(row.totalTimeAssigning);
        const assigned = row.totalAssigned || 0;
        return assigned > 0 ? minutes / assigned : 0;
      },
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'timeSpentFiling',
      title: 'Time spent filing',
      sortable: true,
      filterable: false,
      accessor: (row) => parseDuration(row.totalTimeInReview),
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'timeSpentAssigning',
      title: 'Time spent assigning',
      sortable: true,
      filterable: false,
      accessor: (row) => parseDuration(row.totalTimeAssigning),
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'totalWorkTime',
      title: 'Total time filing and assigning',
      sortable: true,
      filterable: false,
      accessor: (row) => parseDuration(row.totalTimeInReview) + parseDuration(row.totalTimeAssigning),
      renderer: (value) => formatDurationMinutes(value)
    })
  ], [userIdToName]);

  // Define team table columns
  const teamTableColumns = useMemo(() => [
    new Column({
      key: 'teamName',
      title: 'Team Name',
      sortable: true,
      filterable: true,
      dataType: 'text',
      accessor: (row) => row.teamName
    }),
    new Column({
      key: 'memberCount',
      title: 'Members',
      sortable: true,
      filterable: false,
      accessor: (row) => row.memberCount
    }),
    new Column({
      key: 'totalWorked',
      title: 'Documents worked',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalWorked || 0
    }),
    new Column({
      key: 'totalDocs',
      title: 'Documents filed away',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalFiledAway
    }),
    new Column({
      key: 'totalTime',
      title: 'Time reviewing documents',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalTimeMinutes,
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'avgTime',
      title: 'Average time reviewing each document',
      sortable: true,
      filterable: false,
      accessor: (row) => row.avgTimePerDoc,
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'totalAssigned',
      title: 'Documents assigned',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalAssigned || 0
    }),
    new Column({
      key: 'assigningTime',
      title: 'Time assigning documents',
      sortable: true,
      filterable: false,
      accessor: (row) => row.assigningTimeMinutes,
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'avgAssigningTime',
      title: 'Average time assigning each document',
      sortable: true,
      filterable: false,
      accessor: (row) => row.avgAssigningTime,
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'timeSpentFiling',
      title: 'Time spent filing',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalTimeMinutes,
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'timeSpentAssigning',
      title: 'Time spent assigning',
      sortable: true,
      filterable: false,
      accessor: (row) => row.assigningTimeMinutes,
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'totalWorkTime',
      title: 'Total time filing and assigning',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalWorkMinutes,
      renderer: (value) => formatDurationMinutes(value)
    })
  ], []);

  // Calculate task staff aggregate data (COMPLETE actions only)
  const taskStaffAggregateData = useMemo(() => {
    if (!taskData || taskData.length === 0) return [];

    const userTaskCounts = {};

    // Aggregate COMPLETE actions per user across all snapshots
    taskData.forEach(snapshot => {
      const completeAggregates = snapshot.durationSnapshot?.userActionToAggregate?.[UserAction.COMPLETE] || [];
      completeAggregates.forEach(aggregate => {
        if (!userTaskCounts[aggregate.userId]) {
          userTaskCounts[aggregate.userId] = 0;
        }
        userTaskCounts[aggregate.userId] += aggregate.count;
      });
    });

    // Convert to array format for table
    return Object.entries(userTaskCounts).map(([userId, totalCompleted]) => ({
      userId,
      totalCompleted
    })).sort((a, b) => b.totalCompleted - a.totalCompleted);
  }, [taskData]);

  // Calculate task team aggregate data
  const taskTeamAggregateData = useMemo(() => {
    if (!taskStaffAggregateData || taskStaffAggregateData.length === 0 || !teams || teams.length === 0) {
      return [];
    }

    return teams.map(team => {
      const teamStaff = taskStaffAggregateData.filter(staff => 
        team.userIds && team.userIds.includes(staff.userId)
      );

      const uniqueUserIds = new Set(teamStaff.map(s => s.userId));
      const totalCompleted = teamStaff.reduce((sum, staff) => sum + staff.totalCompleted, 0);

      return {
        teamId: team.teamId,
        teamName: team.teamName,
        memberCount: uniqueUserIds.size,
        totalCompleted
      };
    }).sort((a, b) => b.totalCompleted - a.totalCompleted);
  }, [taskStaffAggregateData, teams]);

  // Define task staff table columns
  const taskStaffTableColumns = useMemo(() => [
    new Column({
      key: 'name',
      title: 'Name',
      sortable: true,
      filterable: true,
      dataType: 'text',
      accessor: (row) => userIdToName[row.userId] || row.userId
    }),
    new Column({
      key: 'totalCompleted',
      title: 'Tasks completed',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalCompleted
    })
  ], [userIdToName]);

  // Define task team table columns
  const taskTeamTableColumns = useMemo(() => [
    new Column({
      key: 'teamName',
      title: 'Team Name',
      sortable: true,
      filterable: true,
      dataType: 'text',
      accessor: (row) => row.teamName
    }),
    new Column({
      key: 'memberCount',
      title: 'Members',
      sortable: true,
      filterable: false,
      accessor: (row) => row.memberCount
    }),
    new Column({
      key: 'totalCompleted',
      title: 'Tasks completed',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalCompleted
    })
  ], []);

  // Calculate task staff metrics based on selected rows
  const taskStaffMetrics = useMemo(() => {
    if (!taskStaffAggregateData || taskStaffAggregateData.length === 0) {
      return { totalCompleted: 0 };
    }

    const dataToAggregate = selectedTaskStaffRows.size === 0 
      ? taskStaffAggregateData  // All staff
      : taskStaffAggregateData.filter(row => selectedTaskStaffRows.has(row.userId));
    
    const totalCompleted = dataToAggregate.reduce((sum, row) => sum + row.totalCompleted, 0);
    
    return { totalCompleted };
  }, [taskStaffAggregateData, selectedTaskStaffRows]);

  // Calculate task team metrics based on selected rows
  const taskTeamMetrics = useMemo(() => {
    if (!taskTeamAggregateData || taskTeamAggregateData.length === 0) {
      return { totalCompleted: 0 };
    }

    const dataToAggregate = selectedTaskTeamRows.size === 0 
      ? taskTeamAggregateData  // All teams
      : taskTeamAggregateData.filter(row => selectedTaskTeamRows.has(row.teamId));
    
    const totalCompleted = dataToAggregate.reduce((sum, row) => sum + row.totalCompleted, 0);
    
    return { totalCompleted };
  }, [taskTeamAggregateData, selectedTaskTeamRows]);

  // Combined task metrics that consider both staff and team selections
  const combinedTaskMetrics = useMemo(() => {
    // If both selections are empty, show all data
    if (selectedTaskStaffRows.size === 0 && selectedTaskTeamRows.size === 0) {
      return taskStaffMetrics;
    }

    // Collect all unique user IDs from selections using Sets
    const selectedUserIdsSet = new Set();

    // Add directly selected staff
    selectedTaskStaffRows.forEach(userId => selectedUserIdsSet.add(userId));

    // Add staff from selected teams
    if (selectedTaskTeamRows.size > 0) {
      teams.forEach(team => {
        if (selectedTaskTeamRows.has(team.teamId)) {
          team.userIds.forEach(userId => selectedUserIdsSet.add(userId));
        }
      });
    }

    // Filter staff data to only selected users
    const filteredStaff = taskStaffAggregateData.filter(staff => 
      selectedUserIdsSet.has(staff.userId)
    );

    const totalCompleted = filteredStaff.reduce((sum, row) => sum + row.totalCompleted, 0);
    
    return { totalCompleted };
  }, [taskStaffAggregateData, selectedTaskStaffRows, selectedTaskTeamRows, teams, taskStaffMetrics]);

  const consultationStaffMetrics = useMemo(() => {
    if (!consultationStaffAggregateData || consultationStaffAggregateData.length === 0) {
      return {
        totalEncounters: 0,
        totalTime: 'N/A',
        avgTime: 'N/A'
      };
    }

    const dataToAggregate = selectedConsultationStaffRows.size === 0
      ? consultationStaffAggregateData
      : consultationStaffAggregateData.filter(row => selectedConsultationStaffRows.has(row.userId));

    const encounters = dataToAggregate.reduce((sum, row) => sum + row.totalEncounters, 0);
    const totalTimeMinutes = dataToAggregate.reduce((sum, row) => {
      return sum + parseDuration(row.totalTimeInEncounters);
    }, 0);
    const avgTimePerEncounter = encounters > 0 ? totalTimeMinutes / encounters : 0;

    return {
      totalEncounters: encounters,
      totalTime: formatDurationMinutes(totalTimeMinutes),
      avgTime: formatDurationMinutes(avgTimePerEncounter)
    };
  }, [consultationStaffAggregateData, selectedConsultationStaffRows]);

  const consultationTeamAggregateData = useMemo(() => {
    if (!consultationStaffAggregateData || consultationStaffAggregateData.length === 0 || !teams || teams.length === 0) {
      return [];
    }

    return teams.map(team => {
      const teamStaff = consultationStaffAggregateData.filter(staff =>
        team.userIds && team.userIds.includes(staff.userId)
      );

      const uniqueUserIds = new Set(teamStaff.map(s => s.userId));
      const encounters = teamStaff.reduce((sum, staff) => sum + staff.totalEncounters, 0);
      const totalTimeMinutes = teamStaff.reduce((sum, staff) => {
        return sum + parseDuration(staff.totalTimeInEncounters);
      }, 0);

      return {
        teamId: team.teamId,
        teamName: team.teamName,
        memberCount: uniqueUserIds.size,
        totalEncounters: encounters,
        totalTimeInEncounters: formatDurationMinutes(totalTimeMinutes),
        totalTimeMinutes: totalTimeMinutes,
        avgTimePerEncounter: encounters > 0 ? totalTimeMinutes / encounters : 0
      };
    });
  }, [consultationStaffAggregateData, teams]);

  const combinedConsultationMetrics = useMemo(() => {
    if (selectedConsultationStaffRows.size === 0 && selectedConsultationTeamRows.size === 0) {
      return consultationStaffMetrics;
    }

    const selectedUserIdsSet = new Set();
    selectedConsultationStaffRows.forEach(userId => selectedUserIdsSet.add(userId));

    if (selectedConsultationTeamRows.size > 0) {
      teams.forEach(team => {
        if (selectedConsultationTeamRows.has(team.teamId)) {
          team.userIds.forEach(userId => selectedUserIdsSet.add(userId));
        }
      });
    }

    const filteredStaff = consultationStaffAggregateData.filter(staff =>
      selectedUserIdsSet.has(staff.userId)
    );

    const encounters = filteredStaff.reduce((sum, row) => sum + row.totalEncounters, 0);
    const totalTimeMinutes = filteredStaff.reduce((sum, row) => {
      return sum + parseDuration(row.totalTimeInEncounters);
    }, 0);
    const avgTimePerEncounter = encounters > 0 ? totalTimeMinutes / encounters : 0;

    return {
      totalEncounters: encounters,
      totalTime: formatDurationMinutes(totalTimeMinutes),
      avgTime: formatDurationMinutes(avgTimePerEncounter)
    };
  }, [consultationStaffAggregateData, selectedConsultationStaffRows, selectedConsultationTeamRows, teams, consultationStaffMetrics]);

  const consultationStaffTableColumns = useMemo(() => [
    new Column({
      key: 'name',
      title: 'Name',
      sortable: true,
      filterable: true,
      dataType: 'text',
      accessor: (row) => userIdToName[row.userId] || row.userId
    }),
    new Column({
      key: 'totalEncounters',
      title: 'Encounters',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalEncounters
    }),
    new Column({
      key: 'totalTime',
      title: 'Time in encounters',
      sortable: true,
      filterable: false,
      accessor: (row) => parseDuration(row.totalTimeInEncounters),
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'avgTime',
      title: 'Average recording duration',
      sortable: true,
      filterable: false,
      accessor: (row) => {
        const minutes = parseDuration(row.totalTimeInEncounters);
        return row.totalEncounters > 0 ? minutes / row.totalEncounters : 0;
      },
      renderer: (value) => formatDurationMinutes(value)
    })
  ], [userIdToName]);

  const consultationTeamTableColumns = useMemo(() => [
    new Column({
      key: 'teamName',
      title: 'Team Name',
      sortable: true,
      filterable: true,
      dataType: 'text',
      accessor: (row) => row.teamName
    }),
    new Column({
      key: 'memberCount',
      title: 'Members',
      sortable: true,
      filterable: false,
      accessor: (row) => row.memberCount
    }),
    new Column({
      key: 'totalEncounters',
      title: 'Encounters',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalEncounters
    }),
    new Column({
      key: 'totalTime',
      title: 'Time in encounters',
      sortable: true,
      filterable: false,
      accessor: (row) => row.totalTimeMinutes,
      renderer: (value) => formatDurationMinutes(value)
    }),
    new Column({
      key: 'avgTime',
      title: 'Average recording duration',
      sortable: true,
      filterable: false,
      accessor: (row) => row.avgTimePerEncounter,
      renderer: (value) => formatDurationMinutes(value)
    })
  ], []);

  const combinedConsultationStaffSelection = useMemo(() => {
    const combined = new Set(selectedConsultationStaffRows);

    if (selectedConsultationTeamRows.size > 0) {
      teams.forEach(team => {
        if (selectedConsultationTeamRows.has(team.teamId)) {
          team.userIds.forEach(userId => combined.add(userId));
        }
      });
    }

    return combined;
  }, [selectedConsultationStaffRows, selectedConsultationTeamRows, teams]);

  // Compute combined task staff selection: directly selected staff + staff from selected teams
  const combinedTaskStaffSelection = useMemo(() => {
    const combined = new Set(selectedTaskStaffRows);
    
    // Add all users from selected teams
    if (selectedTaskTeamRows.size > 0) {
      teams.forEach(team => {
        if (selectedTaskTeamRows.has(team.teamId)) {
          team.userIds.forEach(userId => combined.add(userId));
        }
      });
    }
    
    return combined;
  }, [selectedTaskStaffRows, selectedTaskTeamRows, teams]);

  // Get documents ready for review count from current processor state
  const documentsReadyForReview = useMemo(() => 
    currentProcessorState ? currentProcessorState.documentsReadyForReview : 0,
    [currentProcessorState]
  );

  // Get incomplete tasks count from current tasks state
  const incompleteTasks = useMemo(() => 
    currentTasksState ? currentTasksState.incompleteTasks : 0,
    [currentTasksState]
  );

  // Get tasks never opened count from current tasks state
  const tasksNeverOpened = useMemo(() => 
    currentTasksState ? currentTasksState.tasksNeverOpened : 0,
    [currentTasksState]
  );

  // Get tasks assigned count from current tasks state
  const tasksAssigned = useMemo(() => 
    currentTasksState ? currentTasksState.tasksAssigned : 0,
    [currentTasksState]
  );

  const consultationsReadyForReview = useMemo(() =>
    currentConsultationsState ? currentConsultationsState.consultationsReadyForReview : 0,
    [currentConsultationsState]
  );

  const dateRangeText = useMemo(() => {
    const snapshotData = insightsType === 'documents'
      ? data
      : insightsType === 'consultations'
        ? consultationData
        : taskData;
    return formatDateRange(snapshotData);
  }, [data, taskData, consultationData, insightsType]);

  // Compute combined staff selection: directly selected staff + staff from selected teams
  const combinedStaffSelection = useMemo(() => {
    const combined = new Set(selectedStaffRows);
    
    // Add all users from selected teams
    if (selectedTeamRows.size > 0) {
      teams.forEach(team => {
        if (selectedTeamRows.has(team.teamId)) {
          team.userIds.forEach(userId => combined.add(userId));
        }
      });
    }
    
    return combined;
  }, [selectedStaffRows, selectedTeamRows, teams]);

  const hasInsightsData =
    insightsType === 'documents'
      ? data.length > 0
      : insightsType === 'consultations'
        ? consultationData.length > 0
        : taskData.length > 0;

  const handleDownloadStaffSpreadsheet = useCallback(() => {
    if (insightsType === 'documents') {
      downloadStaffDocumentsCSV(staffAggregateData, userIdToName);
    } else if (insightsType === 'consultations') {
      downloadStaffConsultationsCSV(consultationStaffAggregateData, userIdToName);
    } else {
      downloadStaffTasksCSV(taskStaffAggregateData, userIdToName);
    }
  }, [insightsType, staffAggregateData, consultationStaffAggregateData, taskStaffAggregateData, userIdToName]);

  const handleDownloadTeamSpreadsheet = useCallback(() => {
    if (insightsType === 'documents') {
      downloadTeamDocumentsCSV(teamAggregateData);
    } else if (insightsType === 'consultations') {
      downloadTeamConsultationsCSV(consultationTeamAggregateData);
    } else {
      downloadTeamTasksCSV(taskTeamAggregateData);
    }
  }, [insightsType, teamAggregateData, consultationTeamAggregateData, taskTeamAggregateData]);

  const staffSpreadsheetDisabled = insightsType === 'documents'
    ? !staffAggregateData || staffAggregateData.length === 0
    : insightsType === 'consultations'
      ? !consultationStaffAggregateData || consultationStaffAggregateData.length === 0
      : !taskStaffAggregateData || taskStaffAggregateData.length === 0;

  const teamSpreadsheetDisabled = insightsType === 'documents'
    ? !teamAggregateData || teamAggregateData.length === 0
    : insightsType === 'consultations'
      ? !consultationTeamAggregateData || consultationTeamAggregateData.length === 0
      : !taskTeamAggregateData || taskTeamAggregateData.length === 0;

  return {
    selectedStaffRows,
    setSelectedStaffRows,
    selectedTeamRows,
    setSelectedTeamRows,
    selectedTaskStaffRows,
    setSelectedTaskStaffRows,
    selectedTaskTeamRows,
    setSelectedTaskTeamRows,
    selectedConsultationStaffRows,
    setSelectedConsultationStaffRows,
    selectedConsultationTeamRows,
    setSelectedConsultationTeamRows,
    staffTableSortState,
    setStaffTableSortState,
    staffTableFilterValues,
    setStaffTableFilterValues,
    staffTableDisabledFilters,
    setStaffTableDisabledFilters,
    staffTablePaginationState,
    setStaffTablePaginationState,
    teamTableSortState,
    setTeamTableSortState,
    teamTableFilterValues,
    setTeamTableFilterValues,
    teamTableDisabledFilters,
    setTeamTableDisabledFilters,
    teamTablePaginationState,
    setTeamTablePaginationState,
    taskStaffTableSortState,
    setTaskStaffTableSortState,
    taskStaffTableFilterValues,
    setTaskStaffTableFilterValues,
    taskStaffTableDisabledFilters,
    setTaskStaffTableDisabledFilters,
    taskStaffTablePaginationState,
    setTaskStaffTablePaginationState,
    taskTeamTableSortState,
    setTaskTeamTableSortState,
    taskTeamTableFilterValues,
    setTaskTeamTableFilterValues,
    taskTeamTableDisabledFilters,
    setTaskTeamTableDisabledFilters,
    taskTeamTablePaginationState,
    setTaskTeamTablePaginationState,
    consultationStaffTableSortState,
    setConsultationStaffTableSortState,
    consultationStaffTableFilterValues,
    setConsultationStaffTableFilterValues,
    consultationStaffTableDisabledFilters,
    setConsultationStaffTableDisabledFilters,
    consultationStaffTablePaginationState,
    setConsultationStaffTablePaginationState,
    consultationTeamTableSortState,
    setConsultationTeamTableSortState,
    consultationTeamTableFilterValues,
    setConsultationTeamTableFilterValues,
    consultationTeamTableDisabledFilters,
    setConsultationTeamTableDisabledFilters,
    consultationTeamTablePaginationState,
    setConsultationTeamTablePaginationState,
    averageTimeToOpenText,
    averageTimeToReviewText,
    averageTimeToFileAwayText,
    averageRecordingDurationText,
    averageConsultationTimeToReviewText,
    totalDocumentIntake,
    totalTasksCreated,
    totalEncounters,
    staffMetrics,
    teamAggregateData,
    teamMetrics,
    combinedMetrics,
    staffTableColumns,
    teamTableColumns,
    taskStaffAggregateData,
    taskTeamAggregateData,
    taskStaffTableColumns,
    taskTeamTableColumns,
    taskStaffMetrics,
    taskTeamMetrics,
    combinedTaskMetrics,
    consultationStaffMetrics,
    consultationTeamAggregateData,
    combinedConsultationMetrics,
    consultationStaffTableColumns,
    consultationTeamTableColumns,
    combinedConsultationStaffSelection,
    combinedTaskStaffSelection,
    documentsReadyForReview,
    incompleteTasks,
    tasksNeverOpened,
    tasksAssigned,
    consultationsReadyForReview,
    dateRangeText,
    combinedStaffSelection,
    hasInsightsData,
    handleDownloadStaffSpreadsheet,
    handleDownloadTeamSpreadsheet,
    staffSpreadsheetDisabled,
    teamSpreadsheetDisabled,
    getCurrentStateSnapshot,
  };
};

export default useInsightsTables;
